import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environment/environment';
import { PlatformThemeService } from './platform-theme.service';

export interface TenantInstructorInfo {
  id?: number;
  instructorId?: number;
  userId?: number;
  name?: string;
  subDomain?: string;
  brandName?: string;
  logo?: string;
  themeColor?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class SubdomainService {
  private readonly http = inject(HttpClient);
  private readonly platformThemeService = inject(PlatformThemeService);
  private readonly baseUrl = environment.eduBaseURL;

  /** Current active subdomain, or null if on main domain */
  readonly currentSubdomain = signal<string | null>(this.extractSubdomain());

  /** Tenant instructor data if resolved */
  readonly tenantInfo = signal<TenantInstructorInfo | null>(null);

  constructor() {}

  /**
   * Extracts subdomain from the current browser hostname.
   * Examples:
   * - mostafa-omar.ashal.network -> "mostafa-omar"
   * - mostafa-omar.localhost -> "mostafa-omar"
   * - mostafa-omar.new-production-tau.vercel.app -> "mostafa-omar"
   * - localhost / www.ashal.network / ashal.network -> null
   */
  extractSubdomain(): string | null {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }

    // 0. Support query parameter for testing/development (e.g. ?subDomain=ahmed or ?subdomain=ahmed)
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('subDomain') || urlParams.has('subdomain')) {
        const querySub = urlParams.get('subDomain') || urlParams.get('subdomain');
        if (
          querySub &&
          querySub.trim() &&
          querySub.trim().toLowerCase() !== 'none' &&
          querySub.trim().toLowerCase() !== 'main'
        ) {
          const cleanParam = querySub.trim().toLowerCase();
          try {
            sessionStorage.setItem('active_subdomain', cleanParam);
          } catch {}
          return cleanParam;
        } else {
          try {
            sessionStorage.removeItem('active_subdomain');
          } catch {}
          return null;
        }
      }
    } catch {}

    const hostname = window.location.hostname.toLowerCase();

    // 1. IP address (e.g. 127.0.0.1) -> Check session fallback for dev
    if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
      try {
        const sessionSub = sessionStorage.getItem('active_subdomain');
        if (sessionSub) return sessionSub;
      } catch {}
      return null;
    }

    // 2. Localhost support (e.g., ahmed.localhost:4200, or plain localhost:4200)
    if (hostname.endsWith('localhost')) {
      const parts = hostname.split('.');
      if (parts.length > 1 && parts[0] !== 'localhost' && parts[0] !== 'www') {
        return parts[0];
      }
      try {
        const sessionSub = sessionStorage.getItem('active_subdomain');
        if (sessionSub) return sessionSub;
      } catch {}
      return null;
    }

    // 3. Vercel preview domain (e.g., ahmed.new-production-tau.vercel.app)
    if (hostname.endsWith('.vercel.app')) {
      const parts = hostname.replace('.vercel.app', '').split('.');
      if (parts.length > 1 && parts[0] !== 'www') {
        return parts[0];
      }
      return null;
    }

    // 4. Custom domain (e.g., ahmed.ashal.network or ahmed.ashaledu.com)
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const sub = parts[0];
      if (sub !== 'www' && sub !== 'app' && sub !== 'api') {
        return sub;
      }
    }

    return null;
  }

  /**
   * Checks if the app is currently running on a tenant subdomain
   */
  hasSubdomain(): boolean {
    return !!this.currentSubdomain();
  }

  /**
   * Get the current subdomain string or null
   */
  getSubdomain(): string | null {
    return this.currentSubdomain();
  }

  /**
   * Cleans and extracts the subdomain slug (e.g. "http://ahmed.ashalshop.com" -> "ahmed")
   */
  parseSubdomainSlug(raw: string | null | undefined): string | null {
    if (!raw) return null;
    let clean = raw.trim().replace(/^https?:\/\//i, '');
    clean = clean.split('/')[0]; // remove path if any
    const parts = clean.split('.');
    if (parts.length > 1) {
      return parts[0]; // "ahmed"
    }
    return clean;
  }

  /**
   * Builds the target URL with the subdomain placed before the domain.
   * Ensures HTTPS in production.
   * Examples:
   * On localhost:4200 with slug "ahmed" -> "http://ahmed.localhost:4200/teacher/landing"
   * On https://ashalshop.com with raw "http://ahmed.ashalshop.com" -> "https://ahmed.ashalshop.com/teacher/landing"
   */
  buildSubdomainUrl(rawSubdomain: string, targetPath: string = '/teacher/landing'): string {
    const slug = this.parseSubdomainSlug(rawSubdomain);
    if (!slug) {
      return targetPath;
    }

    if (typeof window === 'undefined' || !window.location) {
      return targetPath;
    }

    const currentHostname = window.location.hostname.toLowerCase();
    const currentPort = window.location.port ? `:${window.location.port}` : '';
    const protocol = window.location.protocol;

    // 1. If rawSubdomain is a full domain URL (e.g. "http://ahmed.ashalshop.com" or "ahmed.ashalshop.com")
    if (rawSubdomain.includes('.') && !currentHostname.includes('localhost')) {
      let cleanUrl = rawSubdomain.trim();
      if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
        cleanUrl = `https://${cleanUrl}`;
      } else {
        // Enforce HTTPS
        cleanUrl = cleanUrl.replace(/^http:\/\//i, 'https://');
      }
      cleanUrl = cleanUrl.replace(/\/+$/, '');
      return `${cleanUrl}${targetPath}`;
    }

    // 2. If running locally (localhost)
    if (currentHostname.endsWith('localhost')) {
      return `${protocol}//${slug}.localhost${currentPort}${targetPath}`;
    }

    // 3. If on Vercel preview domain (e.g. new-production-tau.vercel.app)
    if (currentHostname.endsWith('.vercel.app')) {
      return `https://${slug}.${currentHostname}${targetPath}`;
    }

    // 4. If on custom domain (e.g. ashal.network or ashalshop.com)
    const parts = currentHostname.split('.');
    const baseDomain = parts.length > 2 && (parts[0] !== 'www') ? parts.slice(1).join('.') : currentHostname.replace(/^www\./, '');
    return `https://${slug}.${baseDomain}${targetPath}`;
  }

  /**
   * Validates and loads tenant instructor data from backend API
   */
  loadTenantBySubdomain(subdomain?: string): Observable<TenantInstructorInfo | null> {
    const sub = subdomain || this.getSubdomain();
    if (!sub) {
      this.tenantInfo.set(null);
      return of(null);
    }

    return this.platformThemeService.loadPlatformBySubdomain(sub).pipe(
      map((config) => {
        if (config) {
          const info: TenantInstructorInfo = {
            id: config.id,
            instructorId: config.instructorId,
            userId: config.instructorId,
            name: config.name,
            subDomain: config.domain || sub,
            domain: config.domain,
            logo: config.logoUrl,
            themeColor: config.primaryColor,
            primaryColor: config.primaryColor,
            secondaryColor: config.secondaryColor,
            instructorProfileImage: config.instructorProfileImage,
          };
          this.tenantInfo.set(info);
          return info;
        }
        this.tenantInfo.set(null);
        return null;
      }),
      catchError(() => {
        this.tenantInfo.set(null);
        return of(null);
      })
    );
  }
}
